import { useEffect, useRef, useState } from 'react';
import { useLocale, useT, type Locale as UiLocale } from '../i18n/index.js';
import { LandingFlag } from '../ui/LandingFlag.js';

/**
 * The bar every public page wears, and its glyphs.
 *
 * Its own module so the landing page can wear it without importing the blog:
 * the blog index carries every article's text, and borrowing the bar from it
 * put all of that into the landing page's first download.
 */

/** The arrow and the two sheet glyphs. The rest of the landing set is unused. */
const GLYPHS = {
  arrow: 'M4.5 12h14M13 6.5 18.5 12 13 17.5',
  menu: 'M4 7.5h16M4 12h16M4 16.5h16',
  close: 'M6 6l12 12M18 6 6 18',
} as const;

export function BlogIcon({ name }: { name: keyof typeof GLYPHS }) {
  return (
    <svg
      className="blog-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d={GLYPHS[name]} />
    </svg>
  );
}

/**
 * The bar: logo pill, link pill, action pill.
 *
 * Separate pills rather than one bar, because an edge-to-edge bar would cut
 * the landing's full-bleed first screen in half. Once
 * the reader has scrolled the middle pill goes and the other two settle to the
 * corners: where am I, and how do I start. On a phone the link pill is not
 * rendered at all; its links live in a sheet opened from the logo pill.
 */
export function BlogNav({ here = 'blog' }: { here?: 'blog' | 'home' }) {
  const t = useT();
  const { locale, setLocale } = useLocale();
  const [stuck, setStuck] = useState(false);
  const [open, setOpen] = useState(false);
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);

  // Two thresholds, not one: a page resting on a single line flickers between
  // the two shapes on every small scroll.
  //
  // Read once a frame, not once per scroll event: reading `scrollY` inside
  // the event made the browser restyle the page on the spot, several times a
  // frame on a wheel, and that was a visible part of the landing's stutter.
  useEffect(() => {
    let frame = 0;
    const read = () => {
      frame = 0;
      const y = window.scrollY;
      setStuck((was) => y > (was ? 16 : 64));
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(read);
    };
    read();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  // The sheet covers the page, so the page must not scroll behind it, and
  // Escape has to close it the way every other sheet in the product does.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    sheetRef.current?.querySelector<HTMLElement>('a, button')?.focus();
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const links = [
    // `#dna` is the section that says what Krevya does differently. This
    // pointed at `#angles`, which the landing page has not had since its
    // rebuild, so "Features" went to the top of the page and nowhere else.
    { href: '/#dna', label: t('landing.nav.features') },
    { href: '/#how', label: t('landing.nav.how') },
    { href: '/#store', label: t('landing.nav.store') },
    { href: '/#pricing', label: t('landing.nav.pricing') },
    { href: '/blog', label: t('landing.nav.blog'), here: here === 'blog' },
    { href: '/login', label: t('landing.nav.signIn') },
  ];

  const other: UiLocale = locale === 'tr' ? 'en' : 'tr';

  return (
    <>
      <header className="blog-bar" data-stuck={stuck ? 'true' : undefined}>
        <div className="blog-pill blog-pill-logo">
          <a href="/" aria-label="Krevya">
            <img src="/brand/mark-96.webp" alt="" width={24} height={24} />
            {/* The wordmark's own lettering, cut from it, rather than the name
                set in the page's font: the brand's word is drawn, not typed. */}
            <strong>
              <img className="blog-word" src="/brand/krevya-word.webp" alt="" width={61} height={18} />
            </strong>
          </a>
          <button
            type="button"
            className="blog-bar-menu"
            ref={menuButtonRef}
            aria-expanded={open}
            aria-label={t('landing.nav.label')}
            onClick={() => setOpen(true)}
          >
            <BlogIcon name="menu" />
          </button>
        </div>

        <nav className="blog-pill blog-pill-links" aria-label={t('landing.nav.label')}>
          {links.map((link) => (
            <a href={link.href} key={link.href} aria-current={link.here ? 'page' : undefined}>
              {link.label}
            </a>
          ))}
          <button
            type="button"
            className="blog-lang"
            onClick={() => setLocale(other)}
            // Named for the language it switches to. The name starts with the
            // code printed on the button, so someone using voice control can
            // say what they see ("click TR") and reach it.
            aria-label={other === 'tr' ? 'TR, Türkçe' : 'EN, English'}
            lang={other}
          >
            <LandingFlag code={other} />
            <span>{other === 'tr' ? 'TR' : 'EN'}</span>
          </button>
        </nav>

        <div className="blog-pill blog-pill-cta">
          <a href="/register">
            {t('landing.nav.start')}
            <BlogIcon name="arrow" />
          </a>
        </div>
      </header>

      {open ? (
        <div className="blog-sheet" ref={sheetRef} role="dialog" aria-modal="true">
          <div className="blog-sheet-top">
            <a href="/" className="blog-sheet-brand">
              <img src="/brand/mark-96.webp" alt="" width={28} height={28} />
              <img className="blog-word" src="/brand/krevya-word.webp" alt="Krevya" width={68} height={20} />
            </a>
            <button
              type="button"
              className="blog-sheet-close"
              onClick={() => {
                setOpen(false);
                menuButtonRef.current?.focus();
              }}
              aria-label={t('landing.nav.close')}
            >
              <BlogIcon name="close" />
            </button>
          </div>
          <nav aria-label={t('landing.nav.label')}>
            {links.map((link) => (
              <a href={link.href} key={link.href} onClick={() => setOpen(false)}>
                {link.label}
                <BlogIcon name="arrow" />
              </a>
            ))}
          </nav>
          <div className="blog-sheet-langs">
            {(['tr', 'en'] as const).map((code) => (
              <button
                type="button"
                key={code}
                aria-pressed={locale === code}
                onClick={() => {
                  setLocale(code);
                  setOpen(false);
                }}
              >
                <LandingFlag code={code} />
                {code === 'tr' ? 'Türkçe' : 'English'}
              </button>
            ))}
          </div>
          <a className="blog-sheet-cta" href="/register" onClick={() => setOpen(false)}>
            {t('landing.nav.start')}
            <BlogIcon name="arrow" />
          </a>
        </div>
      ) : null}
    </>
  );
}
